import { API_BASE_URL } from "../apiConfig";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "../context/LocationContext";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { FaCamera } from "react-icons/fa";
import "./TaskAssign.css";

// Fix Leaflet Default Icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const MapClickHandler = ({ lat, lng }) => {
  useMapEvents({
    click() {
      window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank");
    }
  });
  return null;
};

const TaskAssignment = () => {
  const { locationStatus } = useLocation();
  let currentUser = null;
  try {
    const sessionStored = sessionStorage.getItem("currentUser");
    currentUser = sessionStored ? JSON.parse(sessionStored) : null;
  } catch (err) {
    console.warn("Failed to parse currentUser from sessionStorage", err);
    currentUser = null;
  }
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState({});
  const [proofStatus, setProofStatus] = useState({});
  const proofInputRefs = useRef({}); // refs for each complaint

  // Fetch complaints from backend
  const fetchComplaints = useCallback(async () => {
    try {
      const sessionStored = sessionStorage.getItem("currentUser");
      const user = sessionStored ? JSON.parse(sessionStored) : null;
      if (!user || user.role !== "driver") return;

      const res = await fetch(`${API_BASE_URL}/api/driver/${user.username}`);
      if (!res.ok) throw new Error(`Failed to fetch tasks: ${res.status}`);
      const data = await res.json();
      const filtered = Array.isArray(data) ? data.filter(c => c.status !== "completed") : [];
      setComplaints(filtered);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchComplaints();
    const interval = setInterval(fetchComplaints, 5000);
    return () => clearInterval(interval);
  }, [fetchComplaints]);

  const handleUploadClick = (complaintId) => {
    const ref = proofInputRefs.current && proofInputRefs.current[complaintId];
    if (ref && typeof ref.click === 'function') ref.click();
  };

  const handleFileChange = (e, complaintId) => {
    const file = e.target.files[0];
    if (!file) return;

    // Store file in complaints array and show selected name
    setComplaints(prev =>
      prev.map(c => (c._id === complaintId ? { ...c, proofImage: file } : c))
    );
  };

  const handleCompleteComplaint = async (complaintId) => {
    const complaint = complaints.find(c => c._id === complaintId);

    if (!complaint || !complaint.proofImage) {
      setProofStatus(prev => ({ ...prev, [complaintId]: "❌ Please upload an image first!" }));
      setTimeout(() => {
        setProofStatus(prev => ({ ...prev, [complaintId]: "" }));
      }, 3000);
      return;
    }

    try {
      setLoading(prev => ({ ...prev, [complaintId]: true }));
      const user = JSON.parse(sessionStorage.getItem("currentUser"));

      const formData = new FormData();
      formData.append("proofImage", complaint.proofImage);
      formData.append("driverName", user.username);

      const res = await fetch(
        `${API_BASE_URL}/api/complaint/complete/${complaintId}`,
        { method: "PUT", body: formData }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      // ✅ Show proof completed message first
      setProofStatus(prev => ({ ...prev, [complaintId]: "✔ Proof Completed and Saved" }));

      // 2.5 sec ke liye message show hone ke baad complaint remove
      setTimeout(() => {
        setProofStatus(prev => ({ ...prev, [complaintId]: "" }));
        setComplaints(prev => prev.filter(c => c._id !== complaintId));
      }, 2500);

    } catch (err) {
      console.error(err);
      setProofStatus(prev => ({ ...prev, [complaintId]: `❌ ${err.message}` }));
      setTimeout(() => {
        setProofStatus(prev => ({ ...prev, [complaintId]: "" }));
      }, 3000);
    } finally {
      setLoading(prev => ({ ...prev, [complaintId]: false }));
    }
  };

  return (
    <div className="task-box">
      <div className="task-header">
        <h5 className="title">Task Assignment</h5>
        
        <div className="location-mini-card">
          <span className="dot" style={{ background: locationStatus.includes("✅") ? "#22c55e" : "#ef4444" }}></span>
          <span className="status-text">{locationStatus}</span>
        </div>
      </div>

      {complaints.length === 0 && <p className="no-tasks">No complaints yet</p>}

      {complaints.map((complaint) => {
        let rawSeverity = "pending";
        if (complaint.isSeverityOverridden && complaint.severity) {
          rawSeverity = complaint.severity.toString();
        } else if (complaint.aiSeverity) {
          rawSeverity = complaint.aiSeverity.toString();
        } else if (complaint.severity) {
          rawSeverity = complaint.severity.toString();
        }
        const sevLower = rawSeverity.toLowerCase();
        let normalizedSeverity = 'pending';
        if (sevLower.includes('high')) normalizedSeverity = 'high';
        else if (sevLower.includes('medium')) normalizedSeverity = 'medium';
        else if (sevLower.includes('low')) normalizedSeverity = 'low';
        else if (sevLower.includes('no')) normalizedSeverity = 'no_severity';

        const displaySeverity = sevLower.includes('high') ? 'High' :
          sevLower.includes('medium') ? 'Medium' :
          sevLower.includes('low') ? 'Low' :
          sevLower.includes('no') ? 'No Severity' :
          (rawSeverity === 'pending' ? 'Pending AI Analysis' : rawSeverity);

          const lat = complaint.location && complaint.location.lat ? complaint.location.lat : 0;
          const lng = complaint.location && complaint.location.lng ? complaint.location.lng : 0;

          return (
          <div className="complaint-card" key={complaint._id}>
          <div className="row">
            <div className="col-md-5">
              <img
                src={
                  complaint.image
                    ? `${API_BASE_URL}/uploads/${complaint.image}`
                    : "https://via.placeholder.com/300"
                }
                alt="waste"
                className="complaint-img"
              />
              <div className="driver-map-container">
                <MapContainer
                  center={[lat, lng]}
                  zoom={15}
                  scrollWheelZoom={false}
                  style={{ height: "180px", width: "100%", borderRadius: "8px", marginTop: "12px", zIndex: 1 }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={[lat, lng]} />
                  <MapClickHandler lat={lat} lng={lng} />
                </MapContainer>
              </div>
            </div>

            <div className="col-md-7 info">
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                <b>Severity:</b>
                <span
                  className={`severity-circle severity-${normalizedSeverity}-circle`}
                  title={displaySeverity}
                />
                {/* Only show admin override/notes when current user is an admin */}
                {currentUser && currentUser.role === "admin" && complaint.isSeverityOverridden && (
                  <span style={{fontSize: "11px", color: "#ef2929", fontWeight: "700"}}>
                    ⚠️ Overridden
                  </span>
                )}
              </div>
              <p>
                <b>Description:</b>
                <br />
                {complaint.description || "No description"}
              </p>
              {currentUser && currentUser.role === "admin" && complaint.adminAction && (
                <p style={{ marginTop: "10px", padding: "8px", background: "#fff5f5", borderLeft: "4px solid #ef2929", fontSize: "12px", color: "#b91c1c", fontWeight: "600" }}>
                  🔔 Admin Action: {complaint.adminAction}
                </p>
              )}
            </div>
          </div>

          <div className="actions">
            {/* Hidden file input */}
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              ref={el => (proofInputRefs.current[complaint._id] = el)}
              onChange={e => handleFileChange(e, complaint._id)}
            />

            {/* Upload button triggers hidden input */}
            <button
              className="upload-btn"
              onClick={() => handleUploadClick(complaint._id)}
              title="Upload Image After Clean"
            >
              <FaCamera />
            </button>

            {/* Show selected image name */}
            {complaint.proofImage && <p className="selected-file">Selected: {complaint.proofImage.name}</p>}

            <button
              className="complete-btn"
              onClick={() => handleCompleteComplaint(complaint._id)}
              disabled={loading[complaint._id]}
              style={{ opacity: loading[complaint._id] ? 0.6 : 1, fontSize: '18px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              title="Mark as Completed"
            >
              {loading[complaint._id] ? "⏳" : "✔"}
            </button>

            {/* Proof status message */}
            {proofStatus[complaint._id] && (
              <p className="proof-status">{proofStatus[complaint._id]}</p>
            )}
          </div>
        </div>
      )})}
    </div>
  );
};

export default TaskAssignment;
